import type { HttpErrorResponse } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvService } from '@core-mkt/services/env/env.service';
import { Brand, XccApiEndpoints } from '@xcc-models';
import { catchError, throwError, type Observable } from 'rxjs';

/*
 * NO timeout/retry wrapper here, deliberately.
 *
 * The xcc-client twin wraps its mint in `timeout(50s)` + one retry, and an earlier revision of this
 * service copied that. Master's browser-to-vendor call had no wrapper, and EPS-4793 is a re-route,
 * so it was removed.
 *
 * It could never have fired anyway, which is the load-bearing reason: the request is already
 * bounded SERVER-side. xcc-api caps the vendor leg at 20s (`HttpModule.register({ timeout: 20000 })`
 * in `ambassador.module.ts`) and API Gateway REST caps the whole request at 29s
 * (`Type: Api` in `apps/xcc-api/deploy/xcc-api-lambda-*.yaml`). A 50s client timeout sits outside
 * both ceilings.
 *
 * The one genuinely unbounded case, unchanged by this PR: a stalled browser-to-xcc-api connection.
 * Angular's HttpClient has no default timeout, so the spinner runs until the user leaves.
 */

/** Response contract for `GET /ambassador/get` (served by xcc-api). */
export interface AmbassadorGetResponse {
  url: string;
}

/**
 * Brands that ship no referral program, so a mint here could only ever enrol a contact that will
 * never receive a usable link.
 *
 * Both are also called out in `targeted-sync.service.ts` (see its PAGE_REFERRAL_SIGNATURES doc
 * comment, "DELIBERATELY ABSENT, not overlooked"): neither resolves a usable group id in xcc-api's
 * `AMBASSADOR_GROUP_ID_BY_BRAND`, so there is no GetAmbassador group to mint against.
 *
 * - `AARP` IS a valid {@link Brand}, so it would otherwise pass the membership check below, reach
 *   xcc-api, and enrol into the Aceable account with `add_to_group_id=''` — a live third-party
 *   write that produces nothing (EPS-4793).
 * - `QA` is a test brand with no {@link Brand} member, so it would fail the membership check anyway.
 *   Listing it here makes the omission a documented decision rather than something a reader has to
 *   infer from the enum. Do NOT alias it to `ACE`: that would mint `share.aceable.com` links on a
 *   test site and enrol test addresses into the live Aceable account.
 *
 * HSP is deliberately NOT listed: unlike aarp and qa it DOES ship a referral program —
 * `targeted-sync.service.ts` carries an `ACE:HSP` row in `PAGE_REFERRAL_SIGNATURES` (host
 * `share.aceable.com`, tokens `A`/`H`) — and xcc-api mints it on its own group and campaign on the
 * Aceable account.
 */
const BRANDS_WITHOUT_REFERRAL_PROGRAM: ReadonlySet<string> = new Set(['AARP', 'QA']);

/**
 * Generic, user-facing copy for a mint that failed or produced an unusable link.
 *
 * Exported and shared rather than redeclared per component: both consumers previously held a
 * byte-identical private copy whose doc comment PROMISED it matched the other. A promise two files
 * have to keep by hand is one edit away from being false, and the tests hardcoded the literal twice
 * more. One export makes it structural.
 *
 * Never surface the raw error in its place: an Angular HttpErrorResponse serializes the failing URL
 * into its message. Before EPS-4793 that was GetAmbassador's key-bearing
 * `/api/v2/<user>/<apiKey>/json/...`; it is now the xcc-api BFF route, which carries no credential
 * -- but it still contains the visitor's own email as a query param, so it stays suppressed.
 */
export const REFERRAL_LINK_ERROR = 'We could not generate your referral link. Please try again.';

/**
 * Mints a per-visitor Refer-A-Friend referral link through the xcc-api `/ambassador/get` route.
 *
 * Deliberately mirrors `apps/xcc-client/src/app/services/lib/ambassador-get/ambassador-get.service.ts`
 * in class name, method name and response interface, so a reader who knows one knows the other.
 *
 * It does NOT mirror the twin's timeout/retry convention, and that difference is intentional -- see
 * the note above the class, which also records why the removal is behaviour-neutral: the chain is
 * bounded server-side at 20s/29s, so a 50s client timeout could never have fired.
 *
 * WHY THE BFF: GetAmbassador groups are ACCOUNT-scoped, and xcc-api's `getCredentials(brand)` is the
 * single place that resolves account AND group together (EPS-4739). Calling the vendor directly from
 * the browser — what this path did before EPS-4793 — meant core-mkt had to keep its own copy of that
 * mapping, shipped the vendor API key in the bundle, and silently enrolled IDS referrers onto the
 * Aceable account (a wrong-account response still returns HTTP 200).
 *
 * THE single core-mkt mint path, as of EPS-4794: the Refer-A-Friend container, the legacy invite
 * modal and the Gladly referral popup all call this. `AmbassadorInviteService` -- which used to
 * serve the latter two by calling the vendor directly with a bundled API key -- is deleted.
 *
 * On `email_new_ambassador`: see the `@param` on {@link AmbassadorGetService.getReferralLink}. Two
 * earlier revisions of this block were wrong about it -- one claimed the route "deliberately never
 * sends" the flag, which stopped being true once EPS-4793 restored it for the invite modal and the
 * popup, and another claimed all three surfaces render the link inline, which the popup does not.
 * Both are gone rather than patched: the parameter doc is the single accurate description, and a
 * second copy here is what let the first one drift.
 */
@Injectable({
  providedIn: 'root',
})
export class AmbassadorGetService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(EnvService);

  /**
   * Fetch the referral link for the given visitor email.
   *
   * FAILS CLOSED — every guard below returns an error WITHOUT issuing any HTTP request, rather than
   * falling back to the legacy direct-to-vendor call. A fallback would make the wrong-account bug
   * silently reachable again (both paths render the same success UI), so a misconfiguration has to
   * surface as the caller's error/retry state instead.
   *
   * @param emailNewAmbassador asks GetAmbassador to send its "new ambassador" confirmation email.
   * Defaults to FALSE, which is what the Refer-A-Friend widget needs: it renders the link inline and
   * accepts an arbitrary typed address, so mailing it would be a spam vector (EPS-4531).
   *
   * The legacy invite modal and the Gladly popup pass `true`. This is PRESERVED LEGACY BEHAVIOUR,
   * not a delivery mechanism the UI depends on: both surfaces sent the flag before the BFF re-route,
   * and EPS-4793 may not change it. The invite modal's step-2 box now renders the link inline as
   * well — the `referalLink` typo that left it empty was fixed in this branch — so the email is a
   * second copy rather than the only copy. Anything reasoning about the mail gate in
   * `AmbassadorApiService` should start from "master sent it", not from "the UI needs it".
   */
  getReferralLink(email: string, emailNewAmbassador = false): Observable<AmbassadorGetResponse> {
    // `EnvService.get` deep-clones on every access — read it once.
    const { xccApiUrl, brandConfig } = this.env.get;

    // `brandConfig.id` (ACE, AA, DEC, IDS, …), NOT `iterable.brandId`: ins, mtg and hsp all report
    // `ACE` there and aarp reports `''`, which would send xcc-api the wrong brand and resolve the
    // wrong group id. Already uppercase for every brand, so `.toUpperCase()` is redundant here — it
    // is kept because it is load-bearing in the xcc-client twin (whose `xccEnv.brand` is lowercase)
    // and because `@IsEnum(Brand)` 400s on a lowercase value if a future brand config drifts.
    const brand = (brandConfig?.id ?? '').toUpperCase();

    if (BRANDS_WITHOUT_REFERRAL_PROGRAM.has(brand)) {
      return throwError(() => new Error(`Brand ${brand} has no referral program; skipping mint.`));
    }

    // Membership by enum value, not a hand-maintained list, so a new Brand member needs no edit here.
    if (!(Object.values(Brand) as string[]).includes(brand)) {
      return throwError(() => new Error(`Brand ${brand || '(none)'} is not a known xcc-api brand; skipping mint.`));
    }

    if (!xccApiUrl) {
      return throwError(() => new Error(`xccApiUrl is not configured for brand ${brand}; skipping mint.`));
    }

    // No custom headers: xcc-api enables bare CORS, so a plain GET stays a "simple" request and
    // never incurs a preflight.
    return this.http
      .get<AmbassadorGetResponse>(`${xccApiUrl}${XccApiEndpoints.ambassadorGet}`, {
        // `'1'` not `true`: xcc-api's ValidationPipe has no `transform`, so the DTO takes a string,
        // and omitting the key entirely is what keeps the RAF path provably mail-free.
        params: emailNewAmbassador ? { email, brand, emailNewAmbassador: '1' } : { email, brand },
      })
      .pipe(
        // Deliberate pass-through, kept as a seam for future error mapping and for symmetry with
        // the xcc-client twin. It transforms nothing today.
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }
}
